/**
 * The AI credits of an agent (GET /api/agent-studio/{bot}/credits; backend controller/credits):
 * balance, plan period, the intelligence levels of answers and of Mo with their prices, packs.
 * One request per agent, shared by every surface that shows credits; refreshed on
 * `ub:usage-updated` (a test chat, a purchase, a Mo run) and after a level change.
 * mode "off" (or "shadow"): the platform still counts messages; surfaces keep their old wording.
 */
import { useEffect, useState } from 'react';
import { studioApi } from '../components/agentStudio/studioApi';

const cache = new Map(); // botId -> summary
const inflight = new Map();
const subscribers = new Set();

function publish(botId) {
  subscribers.forEach((fn) => fn(botId));
}

export function loadAiCredits(botId, { force = false } = {}) {
  if (!botId) return Promise.resolve(null);
  if (!force && cache.has(botId)) return Promise.resolve(cache.get(botId));
  if (inflight.has(botId)) return inflight.get(botId);
  const p = studioApi.get(botId, '/credits')
    .then((data) => {
      cache.set(botId, data);
      publish(botId);
      return data;
    })
    .catch(() => null)
    .finally(() => inflight.delete(botId));
  inflight.set(botId, p);
  return p;
}

export const refreshAiCredits = (botId) => loadAiCredits(botId, { force: true });

export function setAiCredits(botId, data) {
  cache.set(botId, data);
  publish(botId);
}

/** Switch the answer and/or Mo level; resolves to the new summary (throws with .status 402 on a Free plan). */
export async function saveAiCreditLevels(botId, levels) {
  const data = await studioApi.patch(botId, '/credits/levels', levels);
  setAiCredits(botId, data);
  return data;
}

export const creditsOn = (data) => data?.mode === 'on';

export default function useAiCredits(botId) {
  const [data, setData] = useState(() => (botId ? cache.get(botId) || null : null));
  useEffect(() => {
    if (!botId) {
      setData(null);
      return undefined;
    }
    let alive = true;
    setData(cache.get(botId) || null);
    loadAiCredits(botId).then((d) => { if (alive && d) setData(d); });
    const onChange = (id) => { if (alive && id === botId) setData(cache.get(botId) || null); };
    const onUsage = () => { refreshAiCredits(botId); };
    subscribers.add(onChange);
    window.addEventListener('ub:usage-updated', onUsage);
    return () => {
      alive = false;
      subscribers.delete(onChange);
      window.removeEventListener('ub:usage-updated', onUsage);
    };
  }, [botId]);
  return data;
}
